import { useRef, useEffect, useState, useCallback } from "react";

const W = 480;
const H = 320;
const PADDLE_H = 64;
const PADDLE_W = 10;

export default function PongGame({ onGameOver }) {
  const canvasRef = useRef(null);
  const [running, setRunning] = useState(false);
  const [score, setScore] = useState(0);
  const [over, setOver] = useState(false);
  const state = useRef(null);

  const reset = useCallback(() => {
    state.current = {
      playerY: H / 2 - PADDLE_H / 2,
      aiY: H / 2 - PADDLE_H / 2,
      ballX: W / 2,
      ballY: H / 2,
      vx: 3.4,
      vy: 2.2,
      hits: 0,
      up: false,
      down: false,
    };
    setScore(0);
    setOver(false);
  }, []);

  const start = () => {
    reset();
    setRunning(true);
  };

  useEffect(() => {
    if (!running) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let raf;

    const onKey = (e, val) => {
      if (["ArrowUp", "ArrowDown", "w", "s"].includes(e.key)) e.preventDefault();
      const s = state.current;
      if (!s) return;
      if (e.key === "ArrowUp" || e.key === "w") s.up = val;
      if (e.key === "ArrowDown" || e.key === "s") s.down = val;
    };
    const kd = (e) => onKey(e, true);
    const ku = (e) => onKey(e, false);
    const move = (e) => {
      const s = state.current;
      if (!s) return;
      const rect = canvas.getBoundingClientRect();
      const y = ((e.touches ? e.touches[0].clientY : e.clientY) - rect.top) * (H / rect.height);
      s.playerY = Math.max(0, Math.min(H - PADDLE_H, y - PADDLE_H / 2));
    };
    window.addEventListener("keydown", kd);
    window.addEventListener("keyup", ku);
    canvas.addEventListener("mousemove", move);
    canvas.addEventListener("touchmove", move, { passive: true });

    const loop = () => {
      const s = state.current;
      if (!s) return;
      if (s.up) s.playerY = Math.max(0, s.playerY - 5);
      if (s.down) s.playerY = Math.min(H - PADDLE_H, s.playerY + 5);

      s.ballX += s.vx;
      s.ballY += s.vy;
      if (s.ballY < 6 || s.ballY > H - 6) s.vy *= -1;

      // AI follows ball with limited speed
      const target = s.ballY - PADDLE_H / 2;
      s.aiY += Math.max(-4, Math.min(4, target - s.aiY));
      s.aiY = Math.max(0, Math.min(H - PADDLE_H, s.aiY));

      // player paddle collision (left)
      if (s.ballX < 20 + PADDLE_W && s.ballY > s.playerY && s.ballY < s.playerY + PADDLE_H && s.vx < 0) {
        s.vx = Math.abs(s.vx) * 1.04;
        s.vy += (s.ballY - (s.playerY + PADDLE_H / 2)) * 0.04;
        s.hits += 1;
        setScore(s.hits * 10);
      }
      // AI paddle collision (right)
      if (s.ballX > W - 20 - PADDLE_W && s.ballY > s.aiY && s.ballY < s.aiY + PADDLE_H && s.vx > 0) {
        s.vx = -Math.abs(s.vx) * 1.02;
        s.vy += (s.ballY - (s.aiY + PADDLE_H / 2)) * 0.03;
      }
      // player misses
      if (s.ballX < 0) {
        setRunning(false);
        setOver(true);
        onGameOver(s.hits * 10);
        return;
      }
      // ball past AI -> bounce back (player point, keep rally going)
      if (s.ballX > W) {
        s.ballX = W / 2;
        s.ballY = H / 2;
        s.vx = -Math.abs(s.vx);
      }

      // draw
      ctx.fillStyle = "#1a0b2e";
      ctx.fillRect(0, 0, W, H);
      ctx.strokeStyle = "rgba(255,255,255,0.15)";
      ctx.setLineDash([6, 10]);
      ctx.beginPath();
      ctx.moveTo(W / 2, 0);
      ctx.lineTo(W / 2, H);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = "#D4AF37";
      ctx.fillRect(20, s.playerY, PADDLE_W, PADDLE_H);
      ctx.fillStyle = "#b794f4";
      ctx.fillRect(W - 20 - PADDLE_W, s.aiY, PADDLE_W, PADDLE_H);
      ctx.fillStyle = "#fff";
      ctx.beginPath();
      ctx.arc(s.ballX, s.ballY, 6, 0, Math.PI * 2);
      ctx.fill();

      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("keydown", kd);
      window.removeEventListener("keyup", ku);
      canvas.removeEventListener("mousemove", move);
      canvas.removeEventListener("touchmove", move);
    };
  }, [running, onGameOver]);

  return (
    <div className="flex flex-col items-center">
      <canvas
        ref={canvasRef}
        width={W}
        height={H}
        data-testid="pong-canvas"
        className="w-full max-w-[480px] rounded-xl border border-[#6A0DAD] bg-[#1a0b2e] touch-none"
      />
      <p className="font-heading text-sm text-[#4A5568] mt-3">Score: <span className="font-bold text-[#6A0DAD]" data-testid="pong-score">{score}</span></p>
      {!running && (
        <button data-testid="pong-start-btn" onClick={start} className="mt-3 bg-[#6A0DAD] text-white hover:bg-[#58098F] rounded-full px-6 py-2.5 font-heading font-semibold transition-colors">
          {over ? "Play again" : "Start"}
        </button>
      )}
      <p className="text-xs text-[#4A5568] mt-2">Move your paddle with the mouse, arrow keys, or W/S. Keep the rally alive!</p>
    </div>
  );
}
